import { useState, useEffect } from "react";
import {
  isAbortError,
  queryCommissaryDetail,
  queryOrderItemsDetail,
  queryTabManagementDetail,
  queryTransactionsDetail,
  queryWebCheckoutDetail,
} from "../api/newReportingClient";
import {
  CommissaryListResponse,
  OrderItemsListResponse,
  ReportQueryParams,
  TabManagementListResponse,
  TransactionsListResponse,
  WebCheckoutListResponse,
} from "../api/types";

export type DetailResponse =
  | TransactionsListResponse
  | OrderItemsListResponse
  | TabManagementListResponse
  | WebCheckoutListResponse
  | CommissaryListResponse;

export interface DetailQueryState {
  data: DetailResponse | null;
  loading: boolean;
  error: string | null;
}

// Runs GET /reports/detail, picking the endpoint by params.reportType
// (TransactionsList vs OrderItemsList). On any change (or unmount) the previous request is
// ABORTED via AbortController, so a slow stale response can't overwrite newer data or keep
// running server-side. The previous `data` is kept while the new request is in flight.
// Pass `null` to skip the fetch (e.g. before the user clicks Search).
export function useDetailQuery(params: ReportQueryParams | null): DetailQueryState {
  const [state, setState] = useState<DetailQueryState>({
    data: null,
    loading: false,
    error: null,
  });

  const key = params ? JSON.stringify(params) : null;

  useEffect(() => {
    if (!params) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    const controller = new AbortController();
    setState((s) => ({ ...s, loading: true, error: null }));

    const req =
      params.reportType === "OrderItemsList"
        ? queryOrderItemsDetail(params, controller.signal)
        : params.reportType === "TabManagementList"
        ? queryTabManagementDetail(params, controller.signal)
        : params.reportType === "WebCheckoutList"
        ? queryWebCheckoutDetail(params, controller.signal)
        : params.reportType === "CommissaryList"
        ? queryCommissaryDetail(params, controller.signal)
        : queryTransactionsDetail(params, controller.signal);

    req
      .then((data) => {
        if (!controller.signal.aborted) setState({ data, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted || isAbortError(err)) return; // cancelled — leave newer state alone
        setState({
          data: null,
          loading: false,
          error: err instanceof Error ? err.message : "Failed to load detail",
        });
      });

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return state;
}
